import classNames from "classnames";
import { addMonths, addWeeks, format, parseISO, subMonths, subWeeks } from "date-fns";
import { FC, memo, useEffect, useState } from "react";

import { ReactComponent as ArrowIcon } from "assets/images/common/arrow.svg";

import TrendCalendar from "modules/assets/ComplianceTrendChart/TrendCalendar";

import { ComplianceTrend, TrendMode, TrendPoint } from "entities/assets";
import { AssetsApi, PortfoliosApi } from "services/http";

import styles from "./style.module.scss";

const MODES: { key: TrendMode; label: string }[] = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

const TICK_INTERVALS = 5;

const MOCK_VALUES = [
  10, 18, 45, 22, 12, 30, 16, 42, 38, 20, 26, 8, 14, 40, 35, 28, 46, 33, 24, 30, 18, 12, 22, 44, 38, 41, 30, 6, 10, 25,
  28,
];
const MOCK_POINTS: TrendPoint[] = MOCK_VALUES.map((value, index) => ({
  label: `${String(index + 1).padStart(2, "0")}.04.2026`,
  value,
}));

const axisText = (label: string) => (/^\d{2}\.\d{2}\.\d{4}$/.test(label) ? String(Number(label.split(".")[0])) : label);

const BarPlot: FC<{ points: TrendPoint[] }> = ({ points }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  if (points.length === 0) {
    return <div className={styles.empty}>No incident data for the selected period</div>;
  }

  const values = points.map((point) => point.value ?? 0);
  const maxValue = Math.max(1, ...values);
  const step = Math.max(1, Math.ceil(maxValue / TICK_INTERVALS));
  const axisMax = step * TICK_INTERVALS;
  const ticks = Array.from({ length: TICK_INTERVALS + 1 }, (_, index) => (TICK_INTERVALS - index) * step);

  return (
    <div className={styles.plot}>
      <div className={styles.yAxis}>
        {ticks.map((tick) => (
          <span key={tick} className={styles.yLabel}>
            {tick}
          </span>
        ))}
      </div>

      <div className={styles.chart}>
        <div className={styles.grid}>
          {ticks.map((tick) => (
            <span key={tick} className={styles.gridLine} />
          ))}
        </div>

        <div className={styles.bars}>
          {points.map((point, index) => {
            const value = point.value ?? 0;
            const heightPercent = (value / axisMax) * 100;
            const active = index === hovered;

            return (
              <div
                key={`${point.label}-${index}`}
                className={styles.barColumn}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
              >
                {active && (
                  <span className={styles.tooltip} style={{ bottom: `calc(${heightPercent}% + 8px)` }}>
                    <span className={styles.tooltipValue}>{value} incidents</span>
                    <span className={styles.tooltipTime}>{point.label}</span>
                  </span>
                )}

                <span
                  className={classNames(styles.bar, { [styles.barActive]: active })}
                  style={{ height: `${heightPercent}%` }}
                />
              </div>
            );
          })}
        </div>

        <div className={styles.xAxis}>
          {points.map((point, index) => (
            <span key={`${point.label}-${index}`} className={styles.xLabel}>
              {axisText(point.label)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const shiftDate = (iso: string, mode: TrendMode, direction: 1 | -1): string => {
  const base = parseISO(iso);
  if (mode === "month") return format(direction > 0 ? addMonths(base, 1) : subMonths(base, 1), "yyyy-MM-dd");
  return format(direction > 0 ? addWeeks(base, 1) : subWeeks(base, 1), "yyyy-MM-dd");
};

type PropTypes = { id?: string; type?: "asset" | "portfolio" };

const IncidentBarChart: FC<PropTypes> = ({ id, type = "asset" }) => {
  const [mode, setMode] = useState<TrendMode>("week");
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const [trend, setTrend] = useState<ComplianceTrend | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (!id) return;

    let active = true;
    (type === "asset" ? AssetsApi.getIncidentTrend : PortfoliosApi.getIncidentTrend)(id, mode, anchor)
      .then(({ data }) => {
        if (active) setTrend(data);
      })
      .catch(() => {
        if (active) setTrend(null);
      });

    return () => {
      active = false;
    };
  }, [id, mode, anchor, type]);

  const changeMode = (next: TrendMode) => {
    setMode(next);
    setAnchor(undefined);
    setCalendarOpen(false);
  };

  const isDynamic = Boolean(id);
  const points = isDynamic ? (trend?.points ?? []) : MOCK_POINTS;
  const navLabel = isDynamic ? (trend?.label ?? "") : "April 2026";
  const canForward = isDynamic ? Boolean(trend?.can_go_forward) : true;
  const currentDate = trend?.date;

  return (
    <section className={styles.card}>
      <h3 className={styles.title}>Incident frequency trend</h3>

      <div className={styles.controls}>
        <div className={styles.toggle}>
          {MODES.map((item) => (
            <button
              key={item.key}
              type="button"
              className={classNames(styles.toggleButton, { [styles.toggleActive]: item.key === mode })}
              onClick={() => (isDynamic ? changeMode(item.key) : setMode(item.key))}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className={styles.nav}>
          <button
            type="button"
            className={classNames(styles.navButton, styles.navPrev)}
            onClick={() => isDynamic && currentDate && setAnchor(shiftDate(currentDate, mode, -1))}
          >
            <ArrowIcon />
          </button>

          <div className={styles.navLabelWrap}>
            <button
              type="button"
              className={styles.navLabel}
              onClick={() => isDynamic && currentDate && setCalendarOpen((open) => !open)}
            >
              {navLabel}
            </button>

            {isDynamic && calendarOpen && currentDate && (
              <>
                <div className={styles.calBackdrop} onClick={() => setCalendarOpen(false)} />
                <TrendCalendar
                  value={currentDate}
                  onSelect={(date) => {
                    setAnchor(date);
                    setCalendarOpen(false);
                  }}
                />
              </>
            )}
          </div>

          <button
            type="button"
            className={classNames(styles.navButton, !canForward && styles.navButtonDisabled)}
            disabled={isDynamic && !canForward}
            onClick={() => isDynamic && canForward && currentDate && setAnchor(shiftDate(currentDate, mode, 1))}
          >
            <ArrowIcon />
          </button>
        </div>
      </div>

      <BarPlot points={points} />
    </section>
  );
};

export default memo(IncidentBarChart);
